/**
 * 自愈 WebSocket 客户端。
 *
 * 特性：
 *   - 断线自动重连，指数退避（1s, 2s, 4s, ... 上限 15s）
 *   - 待发送队列：断线期间发的消息会缓存，重连后发送
 *   - onMessage 回调收到 protocol 服务端消息
 *   - 心跳：15s 发一次 ping
 */

import type { ServerEnvelope } from '@chat/protocol';
import { parseServer } from '@chat/protocol';

export interface WsClientOptions {
  url: string;
  onMessage: (msg: ServerEnvelope) => void;
  onStateChange?: (state: 'connecting' | 'open' | 'closed') => void;
  beforeReconnect?: () => Promise<string | null>;
  onBanned?: () => void;
  onReconnectFailed?: () => void;
}

export class WsClient {
  private url: string;
  private ws: WebSocket | null = null;
  private pending: string[] = [];
  private closed = false;
  private retry = 0;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private onMessage: (msg: ServerEnvelope) => void;
  private onStateChange?: (state: 'connecting' | 'open' | 'closed') => void;

  constructor(private opts: WsClientOptions) {
    this.url = opts.url;
    this.onMessage = opts.onMessage;
    this.onStateChange = opts.onStateChange;
  }

  connect(): void {
    if (this.closed) return;
    this.onStateChange?.('connecting');
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.addEventListener('open', () => {
      if (this.closed) return;
      this.retry = 0;
      this.onStateChange?.('open');
      for (const msg of this.pending.splice(0)) {
        try { this.ws?.send(msg); } catch { this.pending.unshift(msg); break; }
      }
      this.startPing();
    });

    this.ws.addEventListener('message', (ev) => {
      if (this.closed) return;
      const parsed = parseServer(String(ev.data));
      if (parsed?.t === 'error' && parsed.code === 'banned') {
        this.terminateBanned();
        return;
      }
      if (parsed) this.onMessage(parsed);
    });

    this.ws.addEventListener('close', (ev) => {
      if (this.closed) return;
      if (ev.code === 4003) {
        this.terminateBanned();
        return;
      }
      this.stopPing();
      this.onStateChange?.('closed');
      this.scheduleReconnect();
    });

    this.ws.addEventListener('error', () => {
      try { this.ws?.close(); } catch { /* noop */ }
    });
  }

  send(obj: unknown): void {
    if (this.closed) return;
    const raw = JSON.stringify(obj);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(raw);
    } else {
      this.pending.push(raw);
    }
  }

  close(): void {
    this.closed = true;
    this.pending = [];
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopPing();
    try { this.ws?.close(); } catch { /* noop */ }
  }

  updateUrl(url: string): void {
    this.url = url;
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer !== null) return;
    if (this.retry >= 5) {
      this.close();
      this.opts.onReconnectFailed?.();
      return;
    }
    const delay = Math.min(15_000, 1000 * Math.pow(2, this.retry));
    this.retry++;
    this.reconnectTimer = window.setTimeout(async () => {
      this.reconnectTimer = null;
      if (this.closed) return;
      try {
        // 浏览器不暴露握手 403 正文，通过 REST 确认封禁并刷新凭证。
        if (this.opts.beforeReconnect) {
          const url = await this.opts.beforeReconnect();
          if (this.closed) return;
          if (!url) { this.close(); return; }
          this.updateUrl(url);
        }
        this.connect();
      } catch {
        this.scheduleReconnect();
      }
    }, delay);
  }

  private terminateBanned(): void {
    this.close();
    this.opts.onBanned?.();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ t: 'ping' });
    }, 15_000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}
